import { compose } from 'ramda';
import {
  addWeeks,
  differenceInCalendarDays,
  differenceInSeconds,
  differenceInWeeks,
  parseISO,
} from 'date-fns/esm/fp';

import { pluralize } from '../formatters/strings';

export function differenceInWeeksRounded(start, end) {
  const wholeWeeks = differenceInWeeks(start, end);
  const daysLeft = compose(
    Math.abs,
    differenceInCalendarDays(end),
    addWeeks(wholeWeeks)
  )(start);
  if (daysLeft > 3) return wholeWeeks + 1;
  return wholeWeeks;
}

/**
 * Given a duration object, returns a pluralized string that represents the
 * duration of the game.
 *
 * @param {object} duration - The duration object.
 * @param {string} duration.unit - The unit of the duration. I.E. `week`, `day`.
 * @param {number} duration.value - The value of the duration.
 * @returns {string} The pluralized string combining the unit and value.
 *
 * @example
 *   humanReadableGameDuration({ unit: 2, value: 'week' }) //=> '2 weeks'
 *   humanReadableGameDuration({ unit: 1, value: 'day' }) //=> '1 day'
 */
export function humanReadableGameDuration({ unit, value }) {
  return `${value} ${pluralize(unit, value)}`;
}

/**
 * Returns the number of seconds from now until the provided time.
 *
 * @param {string} futureTime - An ISO8601 string representing the future time.
 * @returns {number} The number of seconds from now until the future time.
 *
 * @example
 *
 *   // Given the current time is '2019-09-17T11:05:00'
 *   //
 *   secondsUntil('2019-09-17T11:06:00'); //=> 60
 */
export const secondsUntil = compose(
  (futureTime) => differenceInSeconds(new Date(), futureTime),
  parseISO
);
